import { Injectable } from '@angular/core';

export interface UpdateFileInfo {
  packageSwVersion: string;
  packageSwInfo: string;
  machineModels: string[];
  isValidFormat: boolean;
  errorMessage?: string;
  isCompatible?: boolean;
  compatibilityError?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UpdateFileParserService {
  private readonly HEADER_LENGTH = 2152;

  private readonly MACHINE_MODELS_MAP: { [key: number]: string } = {
    1: 'Faemina',
    2: 'M200',
    4: 'M40',
    8: 'M100RE',
    16: 'President',
    32: 'E71E',
    64: 'E71 Touch',
    128: 'M26',
    256: 'Prestige',
    512: 'M39',
    1024: 'Slayer Steam Single',
  };

  private readonly DEVICE_TYPE_TO_SERIES_MAP: { [deviceType: string]: string } = {
    'seco-sseries': 'S-SERIES',
    'seco-sys-d18': 'P333',
  };

  private readFileBytes(file: File, start: number, length: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const slice = file.slice(start, start + length);
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(slice);
    });
  }

  private readString(buffer: ArrayBuffer, offset: number, length: number, encoding: string = 'ascii'): string {
    const bytes = new Uint8Array(buffer, offset, length);
    if (encoding === 'utf-8') {
      const decoder = new TextDecoder('utf-8');
      const decoded = decoder.decode(bytes);
      return decoded.replace(/\0/g, '').trimEnd();
    } else {
      let str = '';
      for (let i = 0; i < bytes.length; i++) {
        if (bytes[i] !== 0) {
          str += String.fromCharCode(bytes[i]);
        }
      }
      return str.trimEnd();
    }
  }

  private decodeMachineModels(bitmask: number): string[] {
    const models: string[] = [];
    for (const [bit, modelName] of Object.entries(this.MACHINE_MODELS_MAP)) {
      const bitValue = parseInt(bit);
      if (bitValue <= 0x80000000) {
        if ((bitmask & bitValue) !== 0) {
          models.push(modelName);
        }
      }
    }
    return models;
  }

  private extractVersionFromArtifactName(artifactName: string): string {
    const versionMatch = artifactName.match(/(\d+\.\d+\.\d+)\.\d+/);
    if (versionMatch) {
      return versionMatch[1];
    }
    const simpleVersionMatch = artifactName.match(/(\d+\.\d+\.\d+)/);
    if (simpleVersionMatch) {
      return simpleVersionMatch[1];
    }
    return artifactName;
  }

  private async inflateGzip(data: Uint8Array): Promise<Uint8Array> {
    try {
      const stream = new ReadableStream({
        start(controller) {
          controller.enqueue(data);
          controller.close();
        }
      });
      
      const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
      const response = new Response(decompressedStream);
      const arrayBuffer = await response.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } catch (error) {
      throw new Error('GZIP decompression not supported in this browser');
    }
  }

  private parseTar(data: Uint8Array): { [filename: string]: Uint8Array } {
    const files: { [filename: string]: Uint8Array } = {};
    let offset = 0;

    while (offset < data.length) {
      if (offset + 512 > data.length) break;
      const header = data.slice(offset, offset + 512);
      let filename = '';
      for (let i = 0; i < 100; i++) {
        if (header[i] === 0) break;
        filename += String.fromCharCode(header[i]);
      }
      if (!filename) break;
      let sizeStr = '';
      for (let i = 124; i < 136; i++) {
        if (header[i] === 0 || header[i] === 32) break;
        sizeStr += String.fromCharCode(header[i]);
      }
      const size = parseInt(sizeStr.trim(), 8) || 0;
      offset += 512;
      if (size > 0 && offset + size <= data.length) {
        const fileData = data.slice(offset, offset + size);
        files[filename] = fileData;
        const paddedSize = Math.ceil(size / 512) * 512;
        offset += paddedSize;
      } else {
        break;
      }
    }
    return files;
  }

  async parseSpFile(file: File): Promise<UpdateFileInfo> {
    const headerBuffer = await this.readFileBytes(file, 0, this.HEADER_LENGTH);
    const dataView = new DataView(headerBuffer);
    const packageSwVersion = this.readString(headerBuffer, 32, 32);
    const machineModelsBitmask = dataView.getUint32(24, true);
    const machineModels = this.decodeMachineModels(machineModelsBitmask);
    const packageSwInfo = this.readString(headerBuffer, 1128, 1024, 'utf-8');
    
    const result: UpdateFileInfo = {
      packageSwVersion: packageSwVersion.trim(),
      packageSwInfo: packageSwInfo.trim(),
      machineModels,
      isValidFormat: true,
      isCompatible: true,
    };
    return result;
  }

  async parseMenderFile(file: File): Promise<UpdateFileInfo> {
    try {
      // Read .mender file like a TAR
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const tarFiles = this.parseTar(uint8Array);
      const headerTarGzData = tarFiles['header.tar.gz'];
      if (!headerTarGzData) {
        throw new Error('header.tar.gz not found in .mender file');
      }
      let headerTarData: Uint8Array;
      try {
        headerTarData = await this.inflateGzip(headerTarGzData);
      } catch (gzipError) {
        headerTarData = headerTarGzData;
      }

      const headerFiles = this.parseTar(headerTarData);
      let headerInfoData: Uint8Array | null = null;

      if (headerFiles['header-info']) {
        headerInfoData = headerFiles['header-info'];
      } else if (headerFiles['header/header-info']) {
        headerInfoData = headerFiles['header/header-info'];
      } else {
        const headerInfoFile = Object.keys(headerFiles).find(name => 
          name.includes('header-info') || name.endsWith('header-info')
        );
        if (headerInfoFile) {
          headerInfoData = headerFiles[headerInfoFile];
        }
      }

      if (!headerInfoData) {
        throw new Error('header-info file not found in header archive');
      }

      const headerInfoContent = new TextDecoder('utf-8').decode(headerInfoData);
      const headerInfo = JSON.parse(headerInfoContent);

      let machineModels: string[] = [];
      if (headerInfo.artifact_depends && headerInfo.artifact_depends.device_type) {
        //For mender files, the first element is the family type, used for validation purposes
        machineModels.push(...headerInfo.artifact_depends.device_type);
        if (headerInfo.artifact_depends.device_type.includes('seco-sys-d18')) {
          machineModels.push('SUPERA');
        } else if (headerInfo.artifact_depends.device_type.includes('seco-sseries')) {
          //TODO: Take the machine models from the name of the file instead
          machineModels.push('SECO S-SERIES');
        }
      }

      let packageSwVersion = '-';
      if (headerInfo.artifact_provides && headerInfo.artifact_provides.artifact_name) {
        const artifactName = headerInfo.artifact_provides.artifact_name;
        packageSwVersion = this.extractVersionFromArtifactName(artifactName);
      }
      
      const result: UpdateFileInfo = {
        packageSwVersion: packageSwVersion,
        packageSwInfo: '-',
        machineModels: machineModels,
        isValidFormat: true,
        isCompatible: true,
      };
      
      return result;
      
    } catch (error) {
      return {
        packageSwVersion: '',
        packageSwInfo: '-',
        machineModels: [],
        isValidFormat: false,
        isCompatible: false,
        errorMessage: `Invalid Mender file: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  validateFileCompatibility(fileInfo: UpdateFileInfo, selectedFamily: string): { isValid: boolean; errorMessage?: string } {
    if (!selectedFamily || !fileInfo.machineModels || fileInfo.machineModels.length === 0) {
      return { isValid: true };
    }
    const fileSeries = fileInfo.machineModels.map(deviceType => {
      const mapped = this.DEVICE_TYPE_TO_SERIES_MAP[deviceType];
      return mapped || deviceType;
    });

    const isCompatible = fileSeries.some(series => {
      if (series === selectedFamily) return true;
      if (series.toLowerCase() === selectedFamily.toLowerCase()) return true;
      if (series.toLowerCase().includes(selectedFamily.toLowerCase()) || 
          selectedFamily.toLowerCase().includes(series.toLowerCase())) return true;
      
      return false;
    });

    if (!isCompatible) {
      const supportedFamily = fileSeries[0];
      return {
        isValid: false,
        errorMessage: `Incompatible file: you selected ${selectedFamily} but the file is for ${supportedFamily} machines. Please upload a valid .mender file for the selected family.`
      };
    }

    return { isValid: true };
  }
}
